import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Film, Music2, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section } from '../components/shared/Section';
import { GlassCard } from '../components/shared/GlassCard';
import { Particles } from '../components/shared/Particles';
import { SEO } from '../components/shared/SEO';
import { PORTFOLIO_PLACEHOLDERS, CONTACT_INFO } from '../lib/constants';

const PageHero = () => {
  return (
    <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
      <Particles count={15} />

      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm uppercase tracking-[0.3em] text-primary font-manrope mb-6"
        >
          Our Work
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6"
        >
          Selected Projects
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="font-manrope text-base md:text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          A showcase of our work across audio and video post-production.
        </motion.p>
      </div>
    </section>
  );
};

const ComingSoonBanner = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.8 }}
      className="relative overflow-hidden rounded-2xl glass-heavy p-8 md:p-12 text-center mb-16"
    >
      <div className="hero-glow top-0 left-1/2 -translate-x-1/2" />

      <div className="relative z-10">
        <p className="text-sm uppercase tracking-widest text-primary font-manrope mb-4">
          Coming Soon
        </p>
        <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-4">
          Selected Projects Coming Soon
        </h2>
        <p className="text-muted-foreground font-manrope text-sm md:text-base max-w-lg mx-auto">
          We're currently building our portfolio showcase. In the meantime, feel free to 
          reach out to discuss your project requirements.
        </p>
      </div>
    </motion.div>
  );
};

const PlaceholderCard = ({ project, index }) => {
  const icons = {
    'Music Video': Film,
    'Brand Film': Film,
    'Audio Mastering': Headphones,
  };

  const Icon = icons[project.title] || Music2;

  return (
    <GlassCard delay={index * 0.15} className="group overflow-hidden">
      {/* Image placeholder */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
          <div className="text-center">
            <Icon size={40} className="text-primary mx-auto mb-3" />
            <p className="font-syne text-xl font-semibold text-white">
              {project.title}
            </p>
            <p className="font-manrope text-sm text-muted-foreground">
              {project.category}
            </p>
          </div>
        </div>

        {/* Coming Soon Badge */}
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30">
          <span className="text-xs font-manrope text-primary">Coming Soon</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-xs uppercase tracking-widest text-primary font-manrope mb-2">
          {project.category}
        </p>
        <h3 className="font-syne text-xl font-semibold text-white">
          {project.title}
        </h3>
      </div>
    </GlassCard>
  );
};

const CTASection = () => {
  return (
    <Section>
      <div className="relative overflow-hidden rounded-2xl glass-heavy p-12 md:p-16 text-center">
        <div className="hero-glow -top-32 -left-32" />
        <div className="hero-glow -bottom-32 -right-32" />

        <div className="relative z-10">
          <h2 className="font-syne text-3xl md:text-4xl font-bold text-white mb-4">
            Want to Be Part of Our Portfolio?
          </h2>
          <p className="text-muted-foreground font-manrope text-base max-w-xl mx-auto mb-8">
            Let's create something exceptional together. Your project could be our next showcase piece.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="work-page-cta-primary"
                size="lg"
                className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope px-8"
              >
                Start Your Project
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a
              href={`https://wa.me/${CONTACT_INFO.whatsapp.replace('+', '')}?text=Hi, I'd like to discuss a potential project.`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                data-testid="work-page-cta-secondary"
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 font-manrope px-8"
              >
                WhatsApp Direct
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default function Work() {
  return (
    <div data-testid="work-page">
      <SEO 
        title="Our Work"
        description="Explore our portfolio of cinematic video productions, music videos, brand films, and audio post-production projects for independent artists and brands."
        path="/work"
      />
      <PageHero />

      <Section>
        <ComingSoonBanner />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PORTFOLIO_PLACEHOLDERS.map((project, index) => (
            <PlaceholderCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </Section>

      <CTASection />
    </div>
  );
}
